
import {Options, Vue} from "vue-class-component";
import MainLayout from '@/layouts/MainLayout/MainLayout.vue';
import LogoMain from '@/components/LogoMain/LogoMain.vue'
import WarningPortrait from '@/components/WarningPortrait/WarningPortrait.vue';

import {DataStore, EventStore} from '@/store/index';
import {IIntroductionPanel, ITheme} from "@/models/vimy.models";
import {ICookieConsent} from "@/components/CookieConsent/CookieConsent.ts.vue";
import {translate} from "@/helpers/locale-helper";

import gsap from "gsap";
import {Power1} from "gsap/gsap-core";
import BusEvent from "../../../../shared/js/BusEvent";

import {debounce} from 'lodash';

@Options({
  components: {
    MainLayout,
    LogoMain,
    WarningPortrait
  },
  props: {}
})

export default class IntroductionPage extends Vue {
  declare $refs: {
    nav: HTMLElement,
    breadcrumbImg: any,
    intro3Backgrounds: HTMLElement,
  };

  private _panelIndex: number = 0;

  /**
   * Get/Set
   */
  readonly timePerParagraph: number = 2;
  dynamicExperienceCookie = false;
  visiblePanel: any;
  panelHeight: number = 0;
  dataUnwatch!: Function;

  panels: IIntroductionPanel[] = [];
  nextPanelTimer!: number;
  activeTimeout!: any;
  navTimer!: number;
  animSpeed: number = 1000;
  themes: ITheme[] = [];
  themesUnwatch!: Function;

  intro3timeline!: GSAPTimeline | null;
  mainLogo!: HTMLImageElement | null;

  get panelIndex(): number {
    return this._panelIndex;
  }

  set panelIndex(value: number) {
    const current = value;
    const previous = this._panelIndex;

    // setter
    this._panelIndex = value;

    const progression = (previous < value);

    EventStore.triggerIntroductionProgress({
      progression: progression,
      previousStep: previous,
      step: current,
      isContinue: current === 0 && this.experienceCookie,
      totalSteps: this.panels.length,
      progress: (current) / this.panels.length * 100.0
    });
  }

  get previousSlideTranslation() {
    return translate(this.$i18n.locale, 'introduction.slide_previous');
  }

  get nextSlideTranslation() {
    return translate(this.$i18n.locale, 'introduction.slide_next');
  }

  get startExperienceTranslation() {
    return translate(this.$i18n.locale, 'introduction.experience_start');
  }

  get restartExperienceTranslation() {
    return translate(this.$i18n.locale, 'introduction.experience_restart');
  }

  get continueExperienceTranslation() {
    return translate(this.$i18n.locale, 'introduction.experience_continue');
  }

  mounted() {
    this.panels = DataStore.introductionPanels;

    this.dataUnwatch = DataStore.$watch((t) => {
      return t.isFetching;
    }, (value, legacyValue) => {
      // the new data is not loaded yet
      if (value) {
        return;
      }

      this.panels = DataStore.introductionPanels;
      this.resetPanels();

    });


    if (!this.experienceCookie) {
      document.dispatchEvent(new BusEvent(BusEvent.EVENTS.INTRO_BEGIN_FIRST_TIME));
    }
  }

  unmounted() {
    if (this.dataUnwatch)
      this.dataUnwatch();
  }

  getContent(panel: IIntroductionPanel): string[] {
    let result: string[] = [];

    if (this._panelIndex === 2) {
      result[0] = '';
      for (let paragraph of panel.content) {
        // @ts-ignore
        result[0] += (paragraph[this.$i18n.locale]);
      }
    } else {

      for (let paragraph of panel.content) {
        // @ts-ignore
        result.push(paragraph[this.$i18n.locale]);
      }
    }


    if (this._panelIndex > 0) {
      result = result.map(p => this.insertNonBreakingSpaceNearEnd(p));
    }

    return result;
  }

  insertNonBreakingSpaceNearEnd(text: string): string {

    const texts = text.split(' ');
    const lastWord = texts.pop();

    return texts.join(' ') + '&nbsp;' + lastWord;

  }

  get experienceCookie(): boolean {
    this.dynamicExperienceCookie = this.$cookie.isCookieAvailable("memorial_experience");
    return this.dynamicExperienceCookie;
  }

  get panelCounterClass(): string {

    return 'panel_' + this._panelIndex;

  }

  paragraphCount(panel: IIntroductionPanel): number {
    const count = panel?.content.length ?? 0;
    // @ts-ignore
    return count;
  }

  /**
   * Methods
   */

  cookieConsent(): ICookieConsent | null {
    const consentRaw = localStorage.getItem('cookie-consent');
    if (!consentRaw)
      return null;

    return JSON.parse(consentRaw);
  }

  completeExperience(fromClick: boolean = false): void {
    EventStore.triggerIntroductionProgress({
      progression: true,
      previousStep: 0,
      step: this.panels.length,
      isContinue: false,
      totalSteps: this.panels.length,
      progress: 100.0
    });

    EventStore.triggerIntroductionCompleted(fromClick);

    // Delay so we don't see the "restart experience"
    setTimeout(() => {
      this.dynamicExperienceCookie = true;

      // check if we have consent to create a cookie
      const consent = this.cookieConsent();

      if (!(consent?.consent ?? false))
        return;

      this.$cookie.setCookie('memorial_experience', '1', {
        expire: -1, // Never expire. (Support y,m,d,h,min,s)
      })
    }, 800);

    const eventUI = new BusEvent(BusEvent.EVENTS.PLAY_SOUND, {key: "VM_D_1_", loop: false, group: "UI"});
    document.dispatchEvent(eventUI);

  }

  playSound (key:string, group:string = "UNK", timeout:number = 0)
  {
    return; // we bypass for now
    const eventUI = new BusEvent(BusEvent.EVENTS.PLAY_SOUND, {key, loop: false, group});

    if (timeout > 0)
    {
      setTimeout( () => document.dispatchEvent(eventUI), timeout);
    }
    else
    {
      document.dispatchEvent(eventUI);
    }
  }

  resetExperience(): void {

    this.dynamicExperienceCookie = false;
    this.$cookie.removeCookie('memorial_experience');

    this.resetPanels();

    const eventReset = new BusEvent(BusEvent.EVENTS.RESET_STATE, {});
    document.dispatchEvent(eventReset);

    const eventUI = new BusEvent(BusEvent.EVENTS.PLAY_SOUND, {key: "VM_D_1_", loop: false, group: "UI"});
    document.dispatchEvent(eventUI);
  }


  observeHeight(): void {
    // this.activeTimeout = debounce(this.adjustHeight, 300);
    // window.addEventListener('resize', this.activeTimeout);
    //
    // this.adjustHeight();
  }


  // 2022-03-21 currently not used anymore as we simply switched to css
  adjustHeight(): void {
    //console.log('>>>>>>>>>>>>>>>>>>>>>>>>>>>>>Adjusting height');

    this.visiblePanel = document.querySelector(".panel:not(.fade-leave-to)");
    // trigger next tick everytime this visible panel is viewed
    if (this.visiblePanel) {
      const height = this.visiblePanel.offsetHeight;
      const windowHeight = window.innerHeight;

      //console.log('observeHeight', height, windowHeight);

      if (windowHeight > 767 && height > 50) {

        // height of first panel is toggling 1 pixel every frame for some reasons
        if (Math.abs(this.panelHeight - height) > 2) {
          this.panelHeight = height;
        }

      } else {

        // on mobile, we'll scroll the inside of the
        // panels, rather than the page

        if (typeof this.mainLogo === 'undefined') {
          const tempLogoSelector = document.querySelector('.main-top-logo') as HTMLImageElement;

          if (!tempLogoSelector || typeof tempLogoSelector === 'undefined') {
            return;
          }

          this.mainLogo = tempLogoSelector;
        }

        const logoHeight = this.mainLogo ? this.mainLogo?.clientHeight : 60;
        const leftHeight = windowHeight - logoHeight;

        //console.log('logoHeight', logoHeight, windowHeight, leftHeight)

        if (Math.abs(this.panelHeight - leftHeight) > 2) {
          this.panelHeight = leftHeight;
        }


      }
    }

  }

  startNextPanelTimer(): void {
    // @ts-ignore

    // wanted to give time for the first panel's various animations to play out first
    // 2022-03-08 Client wanted a longer delay before the arrows appear
    const panelDelay = this.panelIndex === 0 ? 17000 : 3000;
    // const panelDelay = 1000;


    if (this.panelIndex !== 2) {
      // we will let the animation/gsap call the shownav when it's done
      this.navTimer = setTimeout(() => {
        this.showNav();
      }, panelDelay);
    }


    // If panel 2
    if (this.panelIndex == 2) {
      this.animateFadingImagesIntoIcon();

    }

    // Else not panel 2
    else {
      this.resetFadingImagesIntoIcon();
    }


  }

  showNav() {
    if (typeof this.$refs.nav === 'undefined')
      return;

    this.$refs.nav?.classList.add('visible');

    /* JPB - Next panel removed by client
    this.nextPanelTimer = setTimeout(() => {
      if (this.panelIndex !== (this.panels.length -1))
        this.nextPanel();
    }, 20 * 1000);
    */
  }

  hideNav() {
    if (typeof this.$refs.nav === 'undefined')
      return;

    this.$refs.nav.classList.remove('visible');
  }

  stopNextPanelTimer(): void {
    if (this.nextPanelTimer)
      clearTimeout(this.nextPanelTimer);

    if (this.navTimer)
      clearTimeout(this.navTimer);
  }

  resetNextPanelTimer(): void {
    this.stopNextPanelTimer();
    this.startNextPanelTimer();
  }

  nextPanel(): void {
    this.hideNav();
    this.panelIndex++;
    this.resetNextPanelTimer();

    const eventUI = new BusEvent(BusEvent.EVENTS.PLAY_SOUND, {key: "VM_D_1_", loop: false, group: "UI"});
    document.dispatchEvent(eventUI);
  }

  prevPanel(): void {
    this.hideNav();
    this.panelIndex--;
    this.resetNextPanelTimer();

    const eventUI = new BusEvent(BusEvent.EVENTS.PLAY_SOUND, {key: "VM_D_1_", loop: false, group: "UI"});
    document.dispatchEvent(eventUI);

  }

  resetPanels(): void {
    // Reset the panel index when we come back to the introduction within the same session.
    this.hideNav();
    this.panelIndex = 0;
    this.stopNextPanelTimer();
    this.startNextPanelTimer();
    this.resetFadingImagesIntoIcon();
  }


  resetFadingImagesIntoIcon(): void {
    if (typeof this.intro3timeline !== 'undefined' || this.intro3timeline !== null) {
      this.intro3timeline?.kill();
      this.intro3timeline = null;
    }

    const frames = Array.from(this.$refs.intro3Backgrounds.querySelectorAll('.frame'));
    // @ts-ignore
    frames.map(el => el.style.opacity = 0);
    this.$refs.breadcrumbImg?.classList.remove('visible');

  }

  animateFadingImagesIntoIcon(): void {


    // GSAP note : Quad easing has been deprecated in 3.*
    // they suggest using Power1 instead
    // https://greensock.com/docs/v2/Easing/Quad

    if (typeof this.intro3timeline !== 'undefined') {
      this.resetFadingImagesIntoIcon();
    }

    const $panel2Breadcrumbs = document.querySelector('.panel_2 .breadcrumb-img') as any;
    if (typeof $panel2Breadcrumbs === 'undefined' || $panel2Breadcrumbs == null || window.getComputedStyle($panel2Breadcrumbs)['display'] !== 'flex') {
      setTimeout(this.animateFadingImagesIntoIcon, 300);
      return;
    }


    const frames = this.$refs.intro3Backgrounds.querySelectorAll('.frame');
    const lastFrame = frames[frames.length - 1] as HTMLDivElement;

    const normalFramesShownDurationSecs = 6;
    const normalFramesOverlapSecs = 2.5; // also the fade In/Out Duratio;

    let firstBreadcrumb = $panel2Breadcrumbs.querySelector('div:first-child') as HTMLDivElement;
    let bcBounds = firstBreadcrumb ? firstBreadcrumb.getBoundingClientRect() : false;
    let bcTop = bcBounds ? bcBounds.top : 0; // also the fade In/Out Duratio;
    let bcLeft = bcBounds ? bcBounds.left : 0;
    let bcWidth = firstBreadcrumb ? firstBreadcrumb.offsetWidth : 0;


    // to line up the last two frames (realistic deuil, illustration-deuil),
    // all the frames use the same aspect ratio...but! we need to clip the illustration
    // to line-up with the icon.. so it's not actually the full image but a proportion of it
    let imgClipWidthRatio = 680 / 1920;
    let lastFrameWidth = lastFrame.offsetWidth;
    let imgClipFinalScale = bcWidth / lastFrameWidth / imgClipWidthRatio; // imgClipLeftPositionRatio = 0.16;
    let imgClipFinalPositionX = bcLeft;

    const sentencesGroup = document.querySelector('.panel_2 .group') as HTMLElement;
    const sentences = sentencesGroup.querySelectorAll('.panel_2 .group p');

    //
    // console.log(
    //   'this.$refs.breadcrumbImg', this.$refs.breadcrumbImg,
    //   'firstBreadcrumb', firstBreadcrumb,
    //   'bcBounds', bcBounds,
    //   'bcTop', bcTop,
    //   'bcLeft', bcLeft,
    //   'bcWidth', bcWidth,
    //   'lastFrame', lastFrame,
    //   'lastFrameWidth', lastFrameWidth,
    //   'imgClipWidthRatio', imgClipWidthRatio,
    //   // 'imgClipWidth', imgClipWidth,
    //   'imgClipFinalScale', imgClipFinalScale,
    //   // 'imgClipLeftPositionRatio', imgClipLeftPositionRatio,
    //   'imgClipFinalPositionX', imgClipFinalPositionX,
    // )

    let me = this;
    this.intro3timeline = gsap.timeline()

      // reset the frames
      .set(frames, {delay: 0, duration: 0, opacity: 0, x: 0, y: 0, scaleX: 1, scaleY: 1})
      .set([sentencesGroup, sentences], {delay: 0, duration: 0, opacity: 0})

      // show the nav early in the animation (not at the end anymore)
      .call(() => {
        setTimeout(() => {
          this.showNav();
        }, 1000);
      })

      // animation starts here
      // building frame comes in
      .to([frames[0], sentences[0]], {duration: 1, opacity: 1, ease: Power1.easeInOut, onStart: () => this.playSound("VM_INTRO_04_")})
      // building frame leaving
      .to(frames[0], {delay: 2, duration: 2, opacity: 0, ease: Power1.easeInOut})

      // statue with the diagonal corner begins
      .to([frames[1], sentences[1]], {
        duration: 2,
        opacity: 1,
        ease: Power1.easeInOut
      }, ">-" + 1)
      // statue with the diagonal corner ends
      .to(frames[1], {delay: 2, duration: 2, opacity: 0, ease: Power1.easeInOut,  onStart: () => this.playSound("VM_INTRO_05_")})

      // photo version of statue begins
      .to([frames[2], sentences[2]], {duration: 2, opacity: 1, ease: Power1.easeInOut, onStart: () => this.playSound("VM_INTRO_06_")}, ">-2")
      // photo version of statue starts leaving
      .to(frames[2], {delay: 2, duration: 2, opacity: 0, ease: Power1.easeInOut, onStart: () =>this.playSound("VM_INTRO_07_")})

      // begin animation madness
      // this is the vectorized frame
      .to(lastFrame , {
        delay: 0,
        duration: 0.75,
        opacity: 1,
        ease: Power1.easeInOut,
        //onStart: () => console.log(499)

      }, "<")


      .to(lastFrame, {
        delay: 0.5,
        duration: 1,
        scaleX: imgClipFinalScale,
        scaleY: imgClipFinalScale,
        ease: Power1.easeInOut,
        onStart: () => console.log(510)
      }, ">")


      .to(lastFrame, {
        // delay: normalFramesShownDurationSecs,
        opacity: 1,
        duration: 1.5,
        delay: 0.25,
        y: firstBreadcrumb.getBoundingClientRect().top * 0.71,
        ease: Power1.easeInOut,
        onStart: () => console.log(520)
      }, "<")

      .to(lastFrame, {
        delay: 0.25,
        duration: 1.5,
        x: imgClipFinalPositionX * 0.71,
        ease: Power1.easeInOut,
        onStart: () => console.log(528)
      }, "<")




      .to(lastFrame, {
        // delay: 0.25,
        duration: 1.5,
        opacity: 0,
        ease: Power1.easeIn,
        onStart: () => {
          // existing code
          // Show Breadcrumb animation first
          this.navTimer = setTimeout(() => {

            //console.log('Breadcrumbs reveal begins... NOW')

            this.$refs.breadcrumbImg?.classList.add('visible');
            this.playSound("VM_INTRO_08_", "", 500)


          }, 10);
        }
      } ,"<+0.45");

  }

  /**
   * Lifecycle hooks.
   */
  activated(): void {
    // this.observeHeight();
    this.resetPanels();

    this.themes = DataStore.themes;


    this.themesUnwatch = DataStore.$watch(t => t.themes, (value, _) => {
      this.themes = value;
    });
  }

  deactivated(): void {
    if (this.activeTimeout) {
      window.removeEventListener('resize', this.activeTimeout);
    }


    if (this.themesUnwatch)
      this.themesUnwatch();

    this.stopNextPanelTimer();
  }
}
